/**
 * Rob Steins promo helpers.
 *
 * Shared by the bulk-add bar (rs-bulk.js), the product-page offer box (<rs-offer>, defined here) and the cart
 * add-ons (rs-cart.js). The numbers come from Theme settings > Rob Steins, rendered into
 * <script id="rs-promo-config"> by snippets/rs-bulk-bar.liquid. The discounts themselves are created in
 * Shopify Admin > Discounts; this file only explains progress toward them, it never applies a discount.
 *
 * All money is in cents (the same unit cart.js uses).
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} PromoConfig
 * @property {number} bogoSize        Every Nth item is free (3 = buy 2 get 1 free)
 * @property {{ at: number, pct: number }} tier1
 * @property {{ at: number, pct: number }} tier2
 * @property {number} shipping        Free shipping threshold in cents
 * @property {string} currency
 * @property {string} locale
 * @property {number[]} exclude       Product ids that never count toward promotions (add-ons)
 */

/** @type {PromoConfig | null} */
let cachedConfig = null;

/** @returns {PromoConfig} */
export function getPromoConfig() {
  if (cachedConfig) return cachedConfig;

  /** @type {Partial<PromoConfig>} */
  let raw = {};
  try {
    raw = JSON.parse(document.getElementById('rs-promo-config')?.textContent || '{}');
  } catch (error) {
    console.warn('[rs-promo] Could not read promo config', error);
  }

  cachedConfig = {
    bogoSize: 3,
    tier1: { at: 7500, pct: 15 },
    tier2: { at: 10000, pct: 20 },
    shipping: 7500,
    currency: 'USD',
    locale: 'en-US',
    exclude: [],
    ...raw,
  };
  return cachedConfig;
}

/**
 * @param {number} cents
 * @param {PromoConfig} [config]
 */
export function formatMoney(cents, config = getPromoConfig()) {
  const whole = Math.round(cents) % 100 === 0;
  try {
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(whole ? 0 : 2)}`;
  }
}

/**
 * Items are either cart.js lines ({ product_id, quantity, original_price, gift_card })
 * or bulk selections ({ productId, quantity, price }).
 * @typedef {{ product_id?: number, productId?: number|string, quantity: number, original_price?: number, price?: number, gift_card?: boolean }} PromoItem
 */

/**
 * @param {PromoItem[]} items
 * @param {PromoConfig} [config]
 */
export function promoState(items, config = getPromoConfig()) {
  let count = 0;
  let subtotal = 0;

  for (const item of items) {
    const productId = Number(item.product_id ?? item.productId);
    if (item.gift_card || config.exclude.includes(productId)) continue;
    count += item.quantity;
    subtotal += (item.original_price ?? item.price ?? 0) * item.quantity;
  }

  const size = Math.max(2, config.bogoSize);
  const free = Math.floor(count / size);
  const toFree = size - (count % size);

  const tier = subtotal >= config.tier2.at ? config.tier2 : subtotal >= config.tier1.at ? config.tier1 : null;
  const nextTier = tier === config.tier2 ? null : tier === config.tier1 ? config.tier2 : config.tier1;

  return {
    count,
    subtotal,
    free,
    toFree,
    tier,
    nextTier,
    toNextTier: nextTier ? Math.max(0, nextTier.at - subtotal) : 0,
    toShipping: Math.max(0, config.shipping - subtotal),
  };
}

/**
 * One-line hint for the bulk-add bar.
 * @param {ReturnType<typeof promoState>} state
 * @param {PromoConfig} [config]
 */
export function hintText(state, config = getPromoConfig()) {
  const free = state.toFree === 1 ? '1 more and it’s free' : `${state.toFree} more and one is free`;
  const tier = state.nextTier
    ? `${formatMoney(state.toNextTier, config)} to ${state.nextTier.pct}% off`
    : `${state.tier?.pct}% off unlocked`;
  return `${free} · ${tier}`;
}

/** @returns {Promise<{ items: PromoItem[] }>} */
export async function fetchCart() {
  return /** @type {any} */ (window).rsFetchCart(); // shared request, see snippets/scripts.liquid
}

/**
 * Product-page offer box: rules plus a live line that includes what is already in the bag.
 *
 * Server markup comes from blocks/rs-offer.liquid. Attributes:
 *   data-price   selected variant price in cents (re-rendered by Horizon when the variant changes)
 *   data-product-id
 */
class RsOffer extends Component {
  requiredRefs = ['live'];

  /** @type {PromoItem[]} */
  #cartItems = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('change', this.#onInput);
    document.addEventListener('input', this.#onInput);
    document.addEventListener('quantity-selector:update', this.#update);
    document.addEventListener('shopify:cart:lines-update', this.#onCartUpdate);
    this.#loadCart();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('change', this.#onInput);
    document.removeEventListener('input', this.#onInput);
    document.removeEventListener('quantity-selector:update', this.#update);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
  }

  async #loadCart() {
    try {
      this.#cartItems = (await fetchCart()).items;
    } catch {
      this.#cartItems = [];
    }
    this.#update();
  }

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') this.#update();
  };

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    if (!(event instanceof CartLinesUpdateEvent)) return;
    event.promise?.then(() => this.#loadCart()).catch(() => {});
  };

  #currentQuantity() {
    const scope = this.closest('.product-details') ?? document;
    const input = scope.querySelector('input[name="quantity"]');
    const value = input instanceof HTMLInputElement ? parseInt(input.value, 10) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  #update = () => {
    const live = this.refs.live;
    if (!(live instanceof HTMLElement)) return;

    const config = getPromoConfig();
    const price = Number(this.dataset.price) || 0;
    const productId = Number(this.dataset.productId) || 0;
    const quantity = this.#currentQuantity();

    const withThis = [...this.#cartItems, { productId, quantity, price }];
    const state = promoState(withThis, config);
    const before = promoState(this.#cartItems, config);

    /** @type {string[]} */
    const parts = [];
    if (state.free > before.free) {
      const n = state.free - before.free;
      parts.push(`With this in your bag, <b>${n} ${n === 1 ? 'item is' : 'items are'} free</b>.`);
    } else if (state.count > 0) {
      const need = state.toFree;
      parts.push(need === 1 ? 'Add <b>1 more</b> and one is free.' : `Add <b>${need} more</b> and one is free.`);
    }
    if (state.nextTier) {
      parts.push(`You’d be <b>${formatMoney(state.toNextTier, config)}</b> from ${state.nextTier.pct}% off.`);
    } else {
      parts.push(`<b>${state.tier?.pct}% off</b> unlocked.`);
    }
    if (state.toShipping === 0) parts.push('Free shipping included.');

    live.innerHTML = parts.join(' ');
  };
}

if (!customElements.get('rs-offer')) {
  customElements.define('rs-offer', RsOffer);
}
